import * as React from 'react';
import moment from 'moment';
import { Tooltip } from '@mui/material';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { AppointmentChangeOrigin, AppointmentChangeReason, AppointmentData, WaitingListItem } from '../../models';
import { AutocompleteTextField, TextInputField } from '../Forms/fields';
import Typography from '../Typography';
import { getLanguage, useTranslations } from '../../store/translations/hooks';
import { TK } from '../../store/translations/translationKeys';
import WaitingListSmsButton from '../Forms/AppointmentForm/WaitingListSmsButton';
import {
    buildWaitingListSmsMessage,
    createAppointmentAndSendSmsFlow,
    getPreferredContactFromClient,
    getWaitingListSmsButtonTooltip,
    hasPhoneNumberForClient,
    WaitingListClientContact,
} from '../Forms/AppointmentForm/waitingList';
import { createDefaultAppointmentChangeReason, hasAppointmentDeletionBlockers, isFutureAppointment } from '../../shared/appointmint/appointmentDeletion';
import AppButton from '../AppButton';

export interface UseAppointmentDeletionFlowOptions {
    appointmentChangeReasons?: string[];
    companyName?: string;
    executeDelete: (appointment: AppointmentData, deleteEntireGroup?: boolean, reason?: AppointmentChangeReason) => Promise<void>;
    getAppointmentTypeName: (appointment: AppointmentData) => string;
    loadWaitingList: (appointment: AppointmentData) => Promise<WaitingListItem[]>;
    loadWaitingListClient: (clientId: string) => Promise<WaitingListClientContact | undefined>;
    onAddAppointmentChangeReason: (reason: string) => void;
    onCreateReplacementAppointment: (appointment: AppointmentData, item: WaitingListItem) => void | Promise<void>;
    onDeleteBlocked: () => void;
    onSmsError: (error: unknown) => void;
    onSmsSent: () => void;
    sendSms: (clientId: string, message: string) => Promise<void>;
}

export const useAppointmentDeletionFlow = ({
    appointmentChangeReasons,
    companyName,
    executeDelete,
    getAppointmentTypeName,
    loadWaitingList,
    loadWaitingListClient,
    onAddAppointmentChangeReason,
    onCreateReplacementAppointment,
    onDeleteBlocked,
    onSmsError,
    onSmsSent,
    sendSms,
}: UseAppointmentDeletionFlowOptions) => {
    const t = useTranslations();
    const loadWaitingListClientRef = React.useRef(loadWaitingListClient);
    const [deletingAppointment, setDeletingAppointment] = React.useState<AppointmentData | null>(null);
    const [pendingDeletionReason, setPendingDeletionReason] = React.useState<AppointmentChangeReason | undefined>(undefined);
    const [confirmedDeletionReason, setConfirmedDeletionReason] = React.useState<AppointmentChangeReason | undefined>(undefined);
    const [recurringDeleteModalOpen, setRecurringDeleteModalOpen] = React.useState(false);
    const [waitingClientsModal, setWaitingClientsModal] = React.useState<{
        appointment: AppointmentData;
        items: WaitingListItem[];
        entireGroup?: boolean;
        reason?: AppointmentChangeReason;
    } | null>(null);
    const [waitingListClients, setWaitingListClients] = React.useState<Record<string, WaitingListClientContact>>({});

    React.useEffect(() => {
        loadWaitingListClientRef.current = loadWaitingListClient;
    }, [loadWaitingListClient]);

    React.useEffect(() => {
        const waitingListClientIds = [...new Set((waitingClientsModal?.items ?? [])
            .map(item => item.clientId)
            .filter((id): id is string => !!id))];

        if (!waitingListClientIds.length) {
            setWaitingListClients({});
            return;
        }

        let cancelled = false;

        Promise.allSettled(waitingListClientIds.map(id => loadWaitingListClientRef.current(id)))
            .then(results => {
                if (cancelled) return;

                const loadedClients = results
                    .filter((result): result is PromiseFulfilledResult<WaitingListClientContact | undefined> => result.status === 'fulfilled')
                    .map(result => result.value)
                    .filter((client): client is WaitingListClientContact => !!client?.id);

                setWaitingListClients(Object.fromEntries(loadedClients.map(client => [client.id, client])));
            })
            .catch(() => {
                if (cancelled) return;
                setWaitingListClients({});
            });

        return () => {
            cancelled = true;
        };
    }, [waitingClientsModal?.items]);

    const reasonOriginMap = React.useMemo(() => ({
        [t(TK.professional)]: AppointmentChangeOrigin.Professional,
        [t(TK.client)]: AppointmentChangeOrigin.Client,
        [t(TK.organization)]: AppointmentChangeOrigin.Organization,
        [t(TK.other)]: AppointmentChangeOrigin.Other,
        [AppointmentChangeOrigin.Professional]: t(TK.professional),
        [AppointmentChangeOrigin.Client]: t(TK.client),
        [AppointmentChangeOrigin.Organization]: t(TK.organization),
        [AppointmentChangeOrigin.Other]: t(TK.other),
    }), [t]);

    const resetDeletionFlow = React.useCallback(() => {
        setDeletingAppointment(null);
        setPendingDeletionReason(undefined);
        setConfirmedDeletionReason(undefined);
        setRecurringDeleteModalOpen(false);
        setWaitingClientsModal(null);
    }, []);

    const executeDeleteAppointment = React.useCallback((appointment: AppointmentData, deleteEntireGroup?: boolean, reason?: AppointmentChangeReason) => {
        return Promise.resolve(executeDelete(appointment, deleteEntireGroup, reason)).finally(() => {
            setDeletingAppointment(null);
        });
    }, [executeDelete]);

    const performDeleteAppointment = React.useCallback(async (
        appointment: AppointmentData,
        deleteEntireGroup?: boolean,
        reason?: AppointmentChangeReason,
    ) => {
        if (isFutureAppointment(appointment) && appointment.professionalId && appointment.type) {
            const items = await loadWaitingList(appointment).catch(() => []);
            if (items.length > 0) {
                setWaitingClientsModal({ appointment, items, entireGroup: deleteEntireGroup, reason });
                return;
            }
        }

        await executeDeleteAppointment(appointment, deleteEntireGroup, reason);
    }, [executeDeleteAppointment, loadWaitingList]);

    const startDeletion = React.useCallback((appointment: AppointmentData) => {
        if (hasAppointmentDeletionBlockers(appointment)) {
            onDeleteBlocked();
            return;
        }

        setDeletingAppointment(appointment);
        setPendingDeletionReason(createDefaultAppointmentChangeReason());
    }, [onDeleteBlocked]);

    const handleDeletionReasonConfirm = React.useCallback(() => {
        if (!deletingAppointment || !pendingDeletionReason) return;

        if (deletingAppointment.recurring) {
            setConfirmedDeletionReason(pendingDeletionReason);
            setRecurringDeleteModalOpen(true);
        } else {
            void performDeleteAppointment(deletingAppointment, undefined, pendingDeletionReason);
        }

        setPendingDeletionReason(undefined);
    }, [deletingAppointment, pendingDeletionReason, performDeleteAppointment]);

    const getWaitingClientContact = React.useCallback((clientId?: string) => {
        return getPreferredContactFromClient(clientId ? waitingListClients[clientId] : undefined);
    }, [waitingListClients]);

    const waitingClientHasPhoneNumber = React.useCallback((clientId?: string) => {
        return hasPhoneNumberForClient(clientId ? waitingListClients[clientId] : undefined);
    }, [waitingListClients]);

    const getWaitingListSmsMessage = React.useCallback((item: WaitingListItem) => {
        const appointment = waitingClientsModal?.appointment;
        const locale = getLanguage();
        const localizedDate = appointment?.date ? moment(appointment.date).locale(locale) : null;
        const day = localizedDate ? localizedDate.format('DD/MM/YYYY') : '';
        const weekDay = localizedDate ? localizedDate.format('dddd') : '';
        const time = [appointment?.start, appointment?.end].filter(Boolean).join(' - ');

        return buildWaitingListSmsMessage(t(TK.waitingListNewSlotSmsTemplate), {
            clientName: item.clientName,
            appointmentType: appointment ? getAppointmentTypeName(appointment) : '',
            professionalName: appointment?.professionalName,
            day,
            weekDay,
            time,
            companyName: companyName ?? '',
        });
    }, [companyName, getAppointmentTypeName, t, waitingClientsModal?.appointment]);

    const closeWaitingListAndDelete = React.useCallback((item: WaitingListItem) => {
        const modalState = waitingClientsModal;
        setWaitingClientsModal(null);
        setConfirmedDeletionReason(undefined);

        if (!modalState) {
            return Promise.resolve();
        }

        return executeDeleteAppointment(modalState.appointment, modalState.entireGroup, modalState.reason).then(() =>
            Promise.resolve(onCreateReplacementAppointment(modalState.appointment, item)).then(() => undefined),
        );
    }, [executeDeleteAppointment, onCreateReplacementAppointment, waitingClientsModal]);

    const handleWaitingListIgnore = React.useCallback(() => {
        const modalState = waitingClientsModal;
        setWaitingClientsModal(null);
        setConfirmedDeletionReason(undefined);
        setDeletingAppointment(null);
        if (modalState) {
            void executeDeleteAppointment(modalState.appointment, modalState.entireGroup, modalState.reason);
        }
    }, [executeDeleteAppointment, waitingClientsModal]);

    const handleCreateAppointmentAndSendSms = React.useCallback((item: WaitingListItem) => {
        return createAppointmentAndSendSmsFlow({
            item,
            hasClientPhone: waitingClientHasPhoneNumber,
            closeWaitingListAndDelete,
            getWaitingListSmsMessage,
            sendSms,
            onSuccess: onSmsSent,
            onError: onSmsError,
        });
    }, [closeWaitingListAndDelete, getWaitingListSmsMessage, onSmsError, onSmsSent, sendSms, waitingClientHasPhoneNumber]);

    const dialogs = (
        <>
            <Modal isOpen={!!pendingDeletionReason} toggle={resetDeletionFlow}>
                <ModalHeader toggle={resetDeletionFlow}>{t(TK.reasonForChange)}</ModalHeader>
                <ModalBody>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-M)' }}>
                        <Typography>{t(TK.pleaseProvideAReason)}</Typography>

                        <AutocompleteTextField
                            id="appointment-deletion-reason-origin-select"
                            label={t(TK.origin)}
                            value={pendingDeletionReason ? reasonOriginMap[pendingDeletionReason.reasonOrigin] as string : t(TK.other)}
                            options={[
                                t(TK.professional),
                                t(TK.client),
                                t(TK.organization),
                                t(TK.other),
                            ]}
                            onValueChanged={(value) => pendingDeletionReason && setPendingDeletionReason({
                                ...pendingDeletionReason,
                                reasonOrigin: reasonOriginMap[value] as AppointmentChangeOrigin,
                            })}
                        />

                        <AutocompleteTextField
                            id="appointment-deletion-reason"
                            label={t(TK.reason)}
                            value={pendingDeletionReason?.reason || ''}
                            options={appointmentChangeReasons || []}
                            onValueChanged={reason => pendingDeletionReason && setPendingDeletionReason({ ...pendingDeletionReason, reason })}
                            onAddNewOption={onAddAppointmentChangeReason}
                        />

                        <TextInputField
                            id="appointment-deletion-reason-details"
                            label={t(TK.reasonDetails)}
                            type="textarea"
                            value={pendingDeletionReason?.reasonDetails ?? ''}
                            onChange={reasonDetails => pendingDeletionReason && setPendingDeletionReason({ ...pendingDeletionReason, reasonDetails })}
                        />
                    </div>
                </ModalBody>
                <ModalFooter>
                    <AppButton
                        id="appointment-deletion-reason-confirm-button"
                        color="success"
                        disabled={
                            !pendingDeletionReason
                            || pendingDeletionReason.reasonOrigin == null
                            || (
                                !pendingDeletionReason.reason?.trim()?.length
                                && !pendingDeletionReason.reasonDetails?.trim()?.length
                            )
                        }
                        onClick={handleDeletionReasonConfirm}
                    >
                        {t(TK.continueButton)}
                    </AppButton>
                    <AppButton
                        id="appointment-deletion-reason-cancel-button"
                        color="secondary"
                        onClick={resetDeletionFlow}
                    >
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={recurringDeleteModalOpen} toggle={resetDeletionFlow}>
                <ModalHeader toggle={resetDeletionFlow}>{t(TK.recurring)}</ModalHeader>
                <ModalBody>{t(TK.recurringDeleteQuestion)}</ModalBody>
                <ModalFooter>
                    <AppButton
                        id="appointment-deletion-recurring-this-only-button"
                        color="danger"
                        onClick={() => {
                            const appointment = deletingAppointment;
                            const reason = confirmedDeletionReason;
                            setRecurringDeleteModalOpen(false);
                            setConfirmedDeletionReason(undefined);
                            if (appointment) {
                                void performDeleteAppointment(appointment, false, reason);
                            }
                        }}
                    >
                        {t(TK.thisEventOnly)}
                    </AppButton>
                    <AppButton
                        id="appointment-deletion-recurring-following-button"
                        color="danger"
                        onClick={() => {
                            const appointment = deletingAppointment;
                            const reason = confirmedDeletionReason;
                            setRecurringDeleteModalOpen(false);
                            setConfirmedDeletionReason(undefined);
                            if (appointment) {
                                void performDeleteAppointment(appointment, true, reason);
                            }
                        }}
                    >
                        {t(TK.thisAndFollowingEvents)}
                    </AppButton>
                    <AppButton
                        id="appointment-deletion-recurring-cancel-button"
                        color="secondary"
                        onClick={resetDeletionFlow}
                    >
                        {t(TK.cancel)}
                    </AppButton>
                </ModalFooter>
            </Modal>

            <Modal isOpen={!!waitingClientsModal} toggle={handleWaitingListIgnore}>
                <ModalHeader toggle={handleWaitingListIgnore}>
                    {t(TK.waitingList)}
                </ModalHeader>
                <ModalBody style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)' }}>
                    <Typography style={{ marginBottom: 'var(--spacer-M)' }}>{t(TK.clientsWaitingForServiceOrProfessional)}</Typography>
                    {waitingClientsModal?.items.map((item, idx) => {
                        const preferredContact = getWaitingClientContact(item.clientId);
                        const canSendSms = waitingClientHasPhoneNumber(item.clientId);
                        const waitingListSmsMessage = getWaitingListSmsMessage(item);
                        const smsButtonTooltip = getWaitingListSmsButtonTooltip(
                            waitingListSmsMessage,
                            canSendSms,
                            t(TK.clientPhoneRequiredForSmsCommunication),
                        );

                        return (
                            <div
                                key={idx}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: 'var(--spacer-XS) 0',
                                    borderBottom: 'var(--border-thin) solid var(--appLigthGrey)',
                                }}
                            >
                                <Tooltip title={item.notes ?? ''} placement="left" disableHoverListener={!item.notes}>
                                    <div>
                                        <span style={{ fontWeight: 500 }}>{item.clientName}</span>
                                        {item.addedAt && <span style={{ marginLeft: 'var(--spacer-XS)', color: 'var(--text-muted)', fontSize: '0.85em' }}>({item.addedAt.substring(0, 10)})</span>}
                                        {preferredContact && (
                                            <div style={{ color: 'var(--text-muted)', fontSize: '0.85em', marginTop: 'var(--spacer-XXS)' }}>
                                                {preferredContact}
                                            </div>
                                        )}
                                    </div>
                                </Tooltip>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacer-XS)', alignItems: 'flex-end' }}>
                                    <AppButton color="success" size="sm" onClick={() => closeWaitingListAndDelete(item)}>
                                        {t(TK.createAppointment)}
                                    </AppButton>
                                    <WaitingListSmsButton
                                        label={t(TK.createAppointmentAndSendSMS)}
                                        tooltip={smsButtonTooltip}
                                        hasClientId={!!item.clientId}
                                        hasClientPhone={canSendSms}
                                        onClick={() => handleCreateAppointmentAndSendSms(item)}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </ModalBody>
                <ModalFooter>
                    <AppButton color="secondary" onClick={handleWaitingListIgnore}>
                        {t(TK.ignore)}
                    </AppButton>
                </ModalFooter>
            </Modal>
        </>
    );

    return {
        dialogs,
        startDeletion,
    };
};
